import { BaseModel, Location, School, User } from 'app/models';
import * as moment from 'moment';

/**
 * For a given date, return the Date objects corresponding to the
 * previous midnight and the next midnight.
 *
 * @param date
 */
export function getDateLimits(date: Date) {
	return { start: moment(date).startOf('day').toDate(), end: moment(date).endOf('day').toDate() };
}

/**
 * Given an object, returns whether it should be included.
 */
export type FilterFunc<T> = (item: T) => boolean;

/**
 * Takes a list of filters and `&&` (ANDs) them together. If one filter
 * rejects an item, the returned filter will too.
 *
 * @param filters An array of filters.
 * @return A filter function
 */
export function mergeFilters<T>(filters: FilterFunc<T>[]): FilterFunc<T> {
	return (item: T) => {
		for (const filter of filters) {
			if (!filter(item)) {
				return false;
			}
		}

		return true;
	};
}

/**
 * Types of filters for hall passes.
 */
export type PassFilterType =
	| { type: 'issuer'; value: User }
	| { type: 'student'; value: User }
	| {
			type: 'location';
			value: Location | Location[];
	  };

export type RequestFilterType =
	| { type: 'issuer'; value: User }
	| { type: 'student'; value: User }
	| {
			type: 'destination';
			value: Location;
	  };

export type WaitingInLineFilter =
	| { type: 'issuer'; value: User }
	| { type: 'student'; value: User }
	| {
			type: 'origin';
			value: Location;
	  };

/**
 * An interface representing how hall passes should be filtered.
 */
export interface HallPassFilter {
	sort: string;
	search_query: string;
}

/**
 * This function generally expects an instance of our HttpService, but for testing
 * purposes, we accept any object that has a `getSchool` method.
 */
export function makeSchoolFilter<T extends { id: number } = School>(http: { getSchool: () => T | null | undefined }) {
	return (obj: BaseModel) => {
		const school = http.getSchool();
		return school == null || obj.isAssignedToSchool(school.id);
	};
}
